import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, orderBy, query, Timestamp } from "firebase/firestore";
import { db } from "@/firebase";

interface PriceAlertItem {
  id: string;
  productId: string;
  variantId: string;
  email: string;
  registeredPrice: number;
  currency: string;
  shopifyProductTitle: string;
  shopifyHandle: string;
  notified: boolean;
  notifiedAt?: Timestamp | null;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
}

type WishlistTab = "all" | "pending" | "notified";

const PAGE_SIZE = 50;

const formatDate = (value?: Timestamp | null) => {
  if (!value) return "-";
  return value.toDate().toLocaleString("ja-JP");
};

const formatPrice = (price: number, currency: string) =>
  `${price.toLocaleString("ja-JP")} ${currency || "JPY"}`;

export default function WishlistPage() {
  const [items, setItems] = useState<PriceAlertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<WishlistTab>("all");
  const [page, setPage] = useState(1);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const listQuery = query(collection(db, "PriceAlert"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(listQuery);
      const data = snapshot.docs.map((docSnap) => {
        const docData = docSnap.data() as Omit<PriceAlertItem, "id">;
        return {
          id: docSnap.id,
          productId: docData.productId || "",
          variantId: docData.variantId || "",
          email: docData.email || "",
          registeredPrice: docData.registeredPrice || 0,
          currency: docData.currency || "JPY",
          shopifyProductTitle: docData.shopifyProductTitle || "",
          shopifyHandle: docData.shopifyHandle || "",
          notified: Boolean(docData.notified),
          notifiedAt: docData.notifiedAt ?? null,
          createdAt: docData.createdAt ?? null,
          updatedAt: docData.updatedAt ?? null,
        };
      });
      setItems(data);
    } catch (err) {
      console.error("Failed to load price alert items", err);
      setError("ウィッシュリストの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const pendingItems = useMemo(() => items.filter((item) => !item.notified), [items]);
  const notifiedItems = useMemo(() => items.filter((item) => item.notified), [items]);
  const tabItems =
    activeTab === "pending" ? pendingItems : activeTab === "notified" ? notifiedItems : items;

  const totalPages = Math.max(1, Math.ceil(tabItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return tabItems.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPage, tabItems]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">ウィッシュリスト</h2>
          <p className="text-sm text-gray-500">
            値下げ通知の登録状況（PriceAlertコレクション）と通知送信状況を表示します。
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => {
                setActiveTab("all");
                setPage(1);
              }}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === "all"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              すべて
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab("pending");
                setPage(1);
              }}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === "pending"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              通知待ち
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab("notified");
                setPage(1);
              }}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === "notified"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              送信済み
            </button>
          </div>
          <button
            type="button"
            onClick={fetchItems}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
          >
            {loading ? "更新中..." : "再読み込み"}
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm border border-gray-100 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">
            {activeTab === "pending" ? "通知待ち一覧" : activeTab === "notified" ? "送信済み一覧" : "全件一覧"}
            <span className="ml-2 text-gray-400 font-normal">({tabItems.length}件)</span>
          </h3>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-gray-500">読み込み中...</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-500">{error}</div>
        ) : tabItems.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">登録データがありません。</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">登録日時</th>
                  <th className="px-4 py-3 text-left font-semibold">メールアドレス</th>
                  <th className="px-4 py-3 text-left font-semibold">商品名</th>
                  <th className="px-4 py-3 text-left font-semibold">登録時価格</th>
                  <th className="px-4 py-3 text-left font-semibold">商品ID / バリアントID</th>
                  <th className="px-4 py-3 text-left font-semibold">送信状況</th>
                  <th className="px-4 py-3 text-left font-semibold">送信日時</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDate(item.createdAt)}</td>
                    <td className="px-4 py-3 text-gray-700">{item.email || "-"}</td>
                    <td className="px-4 py-3 text-gray-700">{item.shopifyProductTitle || "-"}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {formatPrice(item.registeredPrice, item.currency)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {item.productId || "-"} / {item.variantId || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                          item.notified
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-amber-50 text-amber-700"
                        }`}
                      >
                        {item.notified ? "送信済み" : "通知待ち"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDate(item.notifiedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && tabItems.length > 0 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button
            type="button"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage <= 1}
            className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-60"
          >
            前のページ
          </button>
          <span className="text-gray-500">
            {currentPage} / {totalPages} ページ
          </span>
          <button
            type="button"
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage >= totalPages}
            className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-60"
          >
            次のページ
          </button>
        </div>
      )}
    </div>
  );
}
